import { Button } from "@ai-teach/ui-web";
import { ErrorReporter } from "@ai-teach/utils";
import type React from "react";
import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch React rendering errors
 * Automatically logs errors to ErrorReporter
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log to ErrorReporter
    ErrorReporter.critical(error, {
      screen: window.location.pathname,
      action: "reactRender",
      metadata: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="max-w-md w-full panel p-6">
            <h2 className="h4 text-error-400 mb-4">Something went wrong</h2>
            <p className="body mb-4">
              We've encountered an unexpected error. Our team has been notified and is working on a
              fix.
            </p>
            <Button variant="primary" onClick={() => window.location.reload()} className="w-full">
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
